(() => {
  const initSizechart = (root) => {
    if (!root || root.dataset.sizechartInit === 'true') return;
    root.dataset.sizechartInit = 'true';

    const tabButtons = Array.from(root.querySelectorAll('[data-sizechart-tab]'));
    const tabPanels = Array.from(root.querySelectorAll('[data-sizechart-panel]'));

    if (!tabButtons.length || !tabPanels.length) return;

    const setActiveTab = (tabId) => {
      if (!tabId) return;
      const activeButton = tabButtons.find((btn) => btn.dataset.sizechartTab === tabId);
      if (!activeButton) return;

      tabButtons.forEach((btn) => {
        const isActive = btn === activeButton;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      tabPanels.forEach((panel) => {
        const isActive = panel.dataset.sizechartPanel === tabId;
        panel.hidden = !isActive;
        panel.classList.toggle('is-active', isActive);
      });

    };

    root.addEventListener('click', (event) => {
      const tabButton = event.target.closest('[data-sizechart-tab]');
      if (tabButton && root.contains(tabButton)) {
        event.preventDefault();
        setActiveTab(tabButton.dataset.sizechartTab);
        return;
      }
    });

    const initial = tabButtons.find((btn) => btn.classList.contains('is-active')) || tabButtons[0];
    setActiveTab(initial.dataset.sizechartTab);
  };

  const parseNumber = (value) => {
    const cleaned = `${value}`.replace(/[^0-9.]/g, '');
    const parsed = parseFloat(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const escapeAttr = (value) =>
    `${value ?? ''}`
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

  const buildConverterMarkup = (groupName, options = {}) => {
    const pdpClass = options.pdp ? ' pdp-page' : '';
    const predictMarkup = options.pdp && options.showWair
      ? `
        <div class="predict-placement"
          data-predict-product-key="${escapeAttr(options.productKey)}"
          data-predict-product-name="${escapeAttr(options.productName)}"
          data-predict-product-img="${escapeAttr(options.productImg)}">
        </div>
      `
      : '';
    const boldMetricsMarkup = options.pdp && options.showBoldMetrics
      ? '<button type="button" data-boldmetrics-proxy hidden></button>'
      : '';

    return `
      <div class="size-converter__buttons${pdpClass}" data-sizechart-converter-group>
        <label class="custom-radio-button">
          <input type="radio" name="${groupName}" value="in" checked />
          <span class="radiomark">in</span>
        </label>
        <label class="custom-radio-button">
          <input type="radio" name="${groupName}" value="cm" />
          <span class="radiomark">cm</span>
        </label>
        ${predictMarkup}
        ${boldMetricsMarkup}
      </div>
    `;
  };

  const getBoldMetricsButton = () => document.querySelector(
    '#boldmetrics-ssc-default button, #boldmetrics-ssc-default [role="button"], #boldmetrics-ssc-default a'
  );

  const syncBoldMetricsProxies = (root) => {
    const originalButton = getBoldMetricsButton();
    const proxyButtons = root.querySelectorAll('[data-boldmetrics-proxy]');

    proxyButtons.forEach((proxyButton) => {
      if (!originalButton) {
        proxyButton.hidden = true;
        return;
      }

      proxyButton.className = originalButton.className;
      proxyButton.classList.add('boldmetrics-ssc-proxy');
      proxyButton.style.cssText = originalButton.style.cssText;
      proxyButton.innerHTML = originalButton.innerHTML;
      proxyButton.disabled = originalButton.disabled;

      ['aria-label', 'title'].forEach((attribute) => {
        const value = originalButton.getAttribute(attribute);
        if (value) proxyButton.setAttribute(attribute, value);
        else proxyButton.removeAttribute(attribute);
      });

      proxyButton.hidden = false;
    });
  };

  const initBoldMetricsProxies = (root) => {
    const proxyButtons = root.querySelectorAll('[data-boldmetrics-proxy]');
    if (!proxyButtons.length) return;

    proxyButtons.forEach((proxyButton) => {
      proxyButton.addEventListener('click', () => {
        getBoldMetricsButton()?.click();
      });
    });

    syncBoldMetricsProxies(root);

    const boldMetricsRoot = document.getElementById('boldmetrics-ssc-default');
    if (!boldMetricsRoot) return;

    const observer = new MutationObserver(() => syncBoldMetricsProxies(root));
    observer.observe(boldMetricsRoot, {
      attributes: true,
      childList: true,
      subtree: true
    });
  };

  const updateTableValues = (table) => {
    if (!table || table.dataset.sizechartConverted === 'true') return;
    const cells = table.querySelectorAll('.c-modalSizeChart1__tableBody .c-modalSizeChart1__tableData');
    cells.forEach((cell) => {
      if (cell.dataset.sizechartConverted === 'true') return;
      const raw = cell.textContent.trim();
      if (!raw) return;

      let html = '';
      if (raw.includes('-')) {
        const parts = raw.split('-').map((part) => parseNumber(part));
        if (parts.length === 2 && parts[0] !== null && parts[1] !== null) {
          html = `<span class="in">${raw}</span><span class="cm">${(parts[0] * 2.54).toFixed(2)}-${(parts[1] * 2.54).toFixed(2)}</span>`;
        }
      } else {
        const value = parseNumber(raw);
        if (value !== null) {
          html = `<span class="in">${raw}</span><span class="cm">${(value * 2.54).toFixed(2)}</span>`;
        }
      }

      if (html) {
        cell.innerHTML = html;
        cell.dataset.sizechartConverted = 'true';
      }
    });

    table.dataset.sizechartConverted = 'true';
  };

  const initConverters = (root) => {
    if (!root || !root.hasAttribute('data-sizechart-converter')) return;
    const targets = root.querySelectorAll('.c-modalSizeChart1__unit');

    const isPdp = root.dataset.sizechartPdp === 'true';
    const showBoldMetrics = root.dataset.boldmetricsEnabled === 'true';
    const showWair = !showBoldMetrics;
    const predictData = isPdp
      ? {
          productKey: root.dataset.predictProductKey,
          productName: root.dataset.predictProductName,
          productImg: root.dataset.predictProductImg
        }
      : {};

    targets.forEach((target) => {
      if (target.dataset.sizechartConverterInit === 'true') return;
      target.dataset.sizechartConverterInit = 'true';

      const identifier = target.getAttribute('data-size') || 'size';
      const sizeInner = target.closest('[data-coll-name]');
      const additionalIdentifier = sizeInner ? `__${sizeInner.getAttribute('data-coll-name')}` : '';
      const groupName = `option__${identifier}${additionalIdentifier}`;

      target.innerHTML = buildConverterMarkup(groupName, {
        pdp: isPdp,
        showBoldMetrics,
        showWair,
        ...predictData
      });

      const sizeChartBlock = target.closest('.c-modalSizeChart1') || sizeInner;
      if (sizeChartBlock && sizeChartBlock.dataset.sizechartTablesConverted !== 'true') {
        sizeChartBlock.querySelectorAll('.c-modalSizeChart1__table').forEach(updateTableValues);
        sizeChartBlock.dataset.sizechartTablesConverted = 'true';
      }

      let table = null;
      if (sizeChartBlock) {
        table =
          sizeChartBlock.querySelector(`.c-modalSizeChart1__table[data-size="${identifier}"]`) ||
          sizeChartBlock.querySelector('.c-modalSizeChart1__table');
      }

      if (!table) {
        let sibling = target.previousElementSibling;
        while (sibling) {
          if (sibling.classList?.contains('c-modalSizeChart1__table')) {
            table = sibling;
            break;
          }
          sibling = sibling.previousElementSibling;
        }
      }

      if (table && table.parentNode && target.parentNode === table.parentNode) {
        table.parentNode.insertBefore(target, table);
      }

      if (table) {
        updateTableValues(table);
      }

      const converter = target.querySelector('[data-sizechart-converter-group]');
      converter?.addEventListener('change', (event) => {
        if (!table) return;
        const value = event.target?.value;
        table.classList.toggle('show-cm', value === 'cm');
      });
    });

    if (showBoldMetrics) initBoldMetricsProxies(root);
  };

  const syncSizeToggleState = (container) => {
    if (!container) return;
    const activeButton =
      container.querySelector('.c-modalSizeChart1__button--isActive') || container.querySelector('[data-size-toggle]');
    if (!activeButton) return;

    const activeSize = activeButton.getAttribute('data-size-toggle');
    if (!activeSize) return;

    container.querySelectorAll('[data-size]').forEach((el) => {
      const sizeValue = el.getAttribute('data-size');
      el.hidden = sizeValue !== activeSize;
    });
  };

  const initSizeToggles = (root) => {
    const containers = root.querySelectorAll('.c-modalSizeChart1');
    if (!containers.length) return;

    containers.forEach((container) => {
      syncSizeToggleState(container);
    });

    root.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-size-toggle]');
      if (!toggle || !root.contains(toggle)) return;

      const container = toggle.closest('.c-modalSizeChart1');
      if (!container) return;

      event.preventDefault();
      const size = toggle.getAttribute('data-size-toggle');
      if (!size) return;

      container.querySelectorAll('[data-size-toggle]').forEach((btn) => {
        btn.classList.toggle('c-modalSizeChart1__button--isActive', btn === toggle);
      });

      container.querySelectorAll('[data-size]').forEach((el) => {
        el.hidden = el.getAttribute('data-size') !== size;
      });
    });
  };

  const init = () => {
    document.querySelectorAll('[data-sizechart]').forEach((root) => {
      initSizechart(root);
      initConverters(root);
      initSizeToggles(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
